'use client';
import React from 'react';
import Link from 'next/link';
import classNames from 'classnames';
import { JobDetail } from '~/types/job-detail';

interface JobSameProps {
  job: JobDetail;
  isMobile: boolean;
}

/**
 * 「同じ条件の求人を見る」パネルを表示するコンポーネント
 */
export function JobSame({ job, isMobile }: JobSameProps) {
  if (!job) return null;

  return (
    <div className="c-panel -job-detail">
      {/* SP向けタイトル */}
      {isMobile && (
        <h2 className="c-panel__header -no-border u-pl16">
          同じ条件の求人を見る
        </h2>
      )}

      {/* PC向けの左右パディングやタイトル */}
      <div className={classNames({ 'u-pl20 u-pr20': !isMobile })}>
        {!isMobile && <h2 className="c-panel__header">同じ条件の求人を見る</h2>}

        <div className="p-job-detail__information">
          <div className="p-same-jobs u-mt15">
            {/* 資格 */}
            {job?.licenses && job?.licenses?.length > 0 && (
              <div className="p-same-jobs__item">
                <div className="p-same-jobs__item__left">
                  <div className="p-same-jobs__item__left__name">資格</div>
                </div>
                <div className="p-same-jobs__item__right">
                  <div className="p-same-jobs__item__right__links">
                    {job?.licenses.map((license, idx) => (
                      <Link
                        key={idx}
                        href={`/licenses/license_${license.id}`}
                        className="p-same-jobs__link"
                      >
                        {license.name}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* 職種 */}
            {job.categories && job.categories.length > 0 && (
              <div className="p-same-jobs__item">
                <div className="p-same-jobs__item__left">
                  <div className="p-same-jobs__item__left__name">職種</div>
                </div>
                <div className="p-same-jobs__item__right">
                  <div className="p-same-jobs__item__right__links">
                    {job.categories.map((category, idx) => (
                      <Link
                        key={idx}
                        href={`/categories/category_${category.id}`}
                        className="p-same-jobs__link"
                      >
                        {category.name}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* 工事の種類 */}
            {job.types && job.types.length > 0 && (
              <div className="p-same-jobs__item">
                <div className="p-same-jobs__item__left">
                  <div className="p-same-jobs__item__left__name">
                    工事の種類
                  </div>
                </div>
                <div className="p-same-jobs__item__right">
                  <div className="p-same-jobs__item__right__links">
                    {job.types.map((type, idx) => (
                      <Link
                        key={idx}
                        href={`/types/type_${type.id}`}
                        className="p-same-jobs__link"
                      >
                        {type.name}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* 都道府県 */}
            <div className="p-same-jobs__item">
              <div className="p-same-jobs__item__left">
                <div className="p-same-jobs__item__left__name">都道府県</div>
              </div>
              <div className="p-same-jobs__item__right">
                <div className="p-same-jobs__item__right__links">
                  {job.prefecture ? (
                    <>
                      {/* Railsでは「@job.prefecture.area.name ＞」でしたが、
                          こちらでは簡易的にエリア内の都道府県リンクを並べています */}
                      {job.prefecture_area.name} ＞{' '}
                      {job.prefecture_area_prefectures?.map((pref, idx) => (
                        <Link
                          key={idx}
                          href={`/prefectures/prefecture_${pref.id}`}
                          className="p-same-jobs__link"
                        >
                          {pref.name}
                        </Link>
                      ))}
                    </>
                  ) : (
                    <>全国</>
                  )}
                </div>
              </div>
            </div>

            {/* 市区町村 */}
            {job.city && (
              <div className="p-same-jobs__item">
                <div className="p-same-jobs__item__left">
                  <div className="p-same-jobs__item__left__name">市区町村</div>
                </div>
                <div className="p-same-jobs__item__right">
                  <div className="p-same-jobs__item__right__links">
                    <Link
                      href={`/prefectures/prefecture_${job.prefecture?.id}/cities/city_${job.city.id}`}
                      className="p-same-jobs__link"
                    >
                      {job.city.name}
                    </Link>
                  </div>
                </div>
              </div>
            )}

            {/* 近隣の区（東京都の場合のみ） */}
            {job.prefecture?.name === '東京都' &&
              job.city_neighborhoods &&
              job.city_neighborhoods.length > 0 && (
                <div className="p-same-jobs__item">
                  <div className="p-same-jobs__item__left">
                    <div className="p-same-jobs__item__left__name">
                      近隣の区
                    </div>
                  </div>
                  <div className="p-same-jobs__item__right">
                    <div className="p-same-jobs__item__right__links">
                      {job.city_neighborhoods.map((cityN, idx) => (
                        <Link
                          key={idx}
                          href={`/prefectures/prefecture_${job.prefecture?.id}/cities/${cityN.name}`}
                          className="p-same-jobs__link"
                        >
                          {cityN.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              )}

            {/* その他 (契約形態・特徴) */}
            {(job.contracts && job.contracts.length > 0) ||
            (job.features && job.features.length > 0) ? (
              <div className="p-same-jobs__item">
                <div className="p-same-jobs__item__left">
                  <div className="p-same-jobs__item__left__name">その他</div>
                </div>
                <div className="p-same-jobs__item__right">
                  <div className="p-same-jobs__item__right__links">
                    {job.contracts?.map((contract, idx) => (
                      <Link
                        key={idx}
                        href={`/contracts/contract_${contract.id}`}
                        className="p-same-jobs__link"
                      >
                        {contract.name}
                      </Link>
                    ))}
                    {job.features?.map((feature, idx) => (
                      <Link
                        key={idx}
                        href={`/features/feature_${feature.id}`}
                        className="p-same-jobs__link"
                      >
                        {feature.name}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
